import React from 'react';
import { Select } from '@plerionhq/design-system';
import { useNavigate } from '@tanstack/react-router';
import { useDashboards } from '../../api/dashboard';
import { LoadingState } from './LoadingState';

import { useTranslation } from 'react-i18next';

interface DashboardSelectorProps {
  currentDashboardId: string;
  className?: string;
}

interface DashboardOption {
  name: string;
  value: string;
  description: string;
  status: string;
  tenantId: string;
  ownerId: string;
  scope: string;
}

export const DashboardSelector: React.FC<DashboardSelectorProps> = ({
  currentDashboardId,
  className = '',
}) => {
  const { t } = useTranslation('pleri/shared');
  const navigate = useNavigate();

  // Fetch all dashboards
  const dashboardsQuery = useDashboards({
    limit: 50,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const { dashboards = [] } = dashboardsQuery.data || {};

  if (dashboardsQuery.isLoading) {
    return (
      <div className={`flex items-center ${className}`}>
        <LoadingState text={t('loadingDashboards')} />
      </div>
    );
  }

  if (dashboardsQuery.error || dashboards.length === 0) {
    return null;
  }

  // Convert dashboards to select options
  const options: DashboardOption[] = dashboards.map((dashboard) => ({
    name: dashboard.title,
    value: dashboard.dashboardId,
    description: dashboard.description,
    status: dashboard.status,
    tenantId: dashboard.tenantId,
    ownerId: dashboard.ownerId,
    scope: dashboard.scope,
  }));

  const currentIndex = options.findIndex(
    (option) => option.value === currentDashboardId,
  );

  return (
    <div className={className}>
      <Select
        options={options}
        defaultSelectedIndex={currentIndex >= 0 ? currentIndex : -1}
        placeholder={t('selectDashboard')}
        onUpdate={(_, selected) => {
          if (selected) {
            // Navigate to the selected dashboard
            navigate({
              to: '/dashboard/$dashboardId',
              params: { dashboardId: selected.value },
              search: {
                tenantId: selected.tenantId,
                ownerId: selected.ownerId,
                scope: selected.scope,
              },
            });
          }
        }}
        wrapperClassName="w-full"
        hideCloseIcon={true}
      />
    </div>
  );
};
